import {inject, Injectable} from '@angular/core';
import {OAuthService} from 'angular-oauth2-oidc';
import {Observable, merge, Subject} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {ContactAttachedData, IAuthContextProvider} from '@dcp/configurator';
import {CookieService} from '@app/services/cookie.service';
import {ContactAttachedDataService} from '@app/services/contact-attached-data.service';
import {DecodedAccessTokenModel} from '@app/model/decoded-access-token.model';
import jwtDecode from 'jwt-decode';

/**
 * Commerce-UI implementation of IAuthContextProvider.
 * Bridges angular-oauth2-oidc and cookie-based country detection
 * to the shared ChatAdvisorContextService in @dcp/configurator.
 */
@Injectable({providedIn: 'root'})
export class CommerceAuthContextProviderService implements IAuthContextProvider {
  private readonly manualTrigger$ = new Subject<void>();
  private readonly contactAttachedDataService = inject(ContactAttachedDataService);

  readonly authStateChange$: Observable<void>;

  constructor(
    private readonly oAuthService: OAuthService,
    private readonly cookieService: CookieService,
  ) {
    const oauthEvents$ = this.oAuthService.events.pipe(
      filter((e) => ['token_received', 'token_refreshed', 'logout', 'silently_refreshed'].includes(e.type)),
      map(() => undefined as void),
    );

    this.authStateChange$ = merge(oauthEvents$, this.manualTrigger$);

    // Emit initial state so the context service gets a value immediately
    // (deferred to next microtask to avoid constructor-time emission issues)
    Promise.resolve().then(() => this.manualTrigger$.next());
  }

  getAccessToken(): string | null {
    return this.oAuthService.getAccessToken() || null;
  }

  getCountry(): string | null {
    return this.cookieService.getCookie('dcp_session_country') || this.cookieService.getCookie('country') || null;
  }

  isEmployee(): boolean {
    const jwt = this.getAccessToken();
    if (!jwt) return false;
    try {
      const decoded = jwtDecode<DecodedAccessTokenModel>(jwt);
      return decoded.groups?.includes('EMPLOYEE') ?? false;
    } catch {
      return false;
    }
  }

  getContactAttachedData(): ContactAttachedData {
    const cad = this.contactAttachedDataService.getContactAttachedData();
    return cad;
  }
}
